const calculateAngleWithAdjacent = (sideAdjacent: number, hypotenuse: number) => {
  const angleRadians = Math.acos(sideAdjacent / hypotenuse)
  const angleDegrees = (angleRadians * 180) / Math.PI
  return angleDegrees
}

export const buildFx = () => {
  const template = [
    [-0.3857626751121872, -0.38559880497181886],
    [-0.49881458271831, 0.4065691181753186],
    [-0.16445656657046048, 0.21625630532516937],
    [-0.22757891983260836, -0.41880227338544285],
    [-0.420381156356159, -0.03860911426603608],
    [-0.44790307209539915, 0.3211866759512054],
    [-0.33942831668816376, -0.2956162334975967],
    [-0.16852482707549088, -0.1583991982135573],
    [-0.3867166926814549, -0.4112092660675153],
  ]

  const [r, margin] = template[Math.floor(Math.random() * template.length - 1) + 1]
  return (x) => r * (Math.sin(x - Math.asin(margin)) + margin)
}

export class AirdropGiftAnimation {
  private _element: HTMLElement
  private _startElement: HTMLElement
  private _endElement: HTMLElement

  private _f: (x: number) => number
  public _points: [number, number][]
  private _startPoint: [number, number]
  private _endPoint: [number, number]
  private _deg: number
  private _distance: number
  private _timeout: any
  private _currentPointIndex = 0
  private _config = { duration: 2700, startAt: 2 * Math.PI, endAt: 0, fps: 65 }
  private _callback = null

  public get currentPoint() {
    if (this._currentPointIndex === this._points.length - 1) {
      return null
    }

    const { angleRadian, x, y } = this._calculateSpecificPoint(this._currentPointIndex)
    return { angleRadian, x, y }
  }

  constructor(element: HTMLElement, startElement: HTMLElement, endElement: HTMLElement, f: (x: number) => number) {
    this._element = element
    this._startElement = startElement
    this._endElement = endElement
    this._f = f
    this.refresh()
  }

  public _calculateSpecificPoint(paramPointIndex) {
    const currentPointIndex = paramPointIndex || this._currentPointIndex
    const [x, y] = this._transformPointWithDeg(this._points[currentPointIndex])

    const [nextX, nextY] =
      currentPointIndex + 1 >= this._points.length
        ? [x, y + 1]
        : this._transformPointWithDeg(this._points[currentPointIndex + 1])

    const deltaX = nextX - x
    const deltaY = nextY - y
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
    const angleRadian = calculateAngleWithAdjacent(deltaY, distance) * (nextX > x ? 1 : -1)
    return { angleRadian, x, y }
  }

  private _calculateEndPoint() {
    const endRect = this._endElement.getBoundingClientRect()
    this._endPoint = [endRect.left + this._endElement.clientWidth / 2, endRect.top + this._endElement.clientHeight / 2]
  }

  private _calculateStartPoint() {
    const startRect = this._startElement.getBoundingClientRect()

    this._startPoint = [
      startRect.left + this._startElement.clientWidth / 2,
      startRect.top + this._startElement.clientHeight / 2,
    ]
  }

  private _calculateDeg() {
    const deltaX = this._endPoint[0] - this._startPoint[0]
    const deltaY = this._endPoint[1] - this._startPoint[1]
    this._deg = calculateAngleWithAdjacent(deltaY, this._distance) * (deltaX > 0 ? -1 : 1)
  }

  private _calculateDistance() {
    const deltaX = this._endPoint[0] - this._startPoint[0]
    const deltaY = this._endPoint[1] - this._startPoint[1]
    this._distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
  }

  private _buildGraph = () => {
    const graphPoints: [number, number][] = []
    const graphPointsLength = (this._config.duration * this._config.fps) / 1000
    const roundValue = 1 / ((this._config.startAt - this._config.endAt) / (graphPointsLength - 1))
    const marginContainerHeight = this._distance / (graphPointsLength - 1)

    for (let step = 0; step <= graphPointsLength; step++) {
      const y = step
      const x = Math.round(this._f(step / roundValue) * roundValue * 1000) / 1000

      graphPoints.push([x * marginContainerHeight, y * marginContainerHeight])
    }

    this._points = graphPoints
  }

  private _transformPointWithDeg([x, y]: [number, number]): [number, number] {
    const deg = this._deg
    return [
      x * Math.cos((Math.PI * deg) / 180) - y * Math.sin((Math.PI * deg) / 180),
      x * Math.sin((Math.PI * deg) / 180) + y * Math.cos((Math.PI * deg) / 180),
    ]
  }

  private _translate(callback?: () => Promise<void> | void) {
    this._callback = callback
    this._timeout = setTimeout(async () => {
      if (!this._element) return

      const currentPoint = this.currentPoint
      if (!currentPoint) {
        const { x, y } = this._calculateSpecificPoint(this._currentPointIndex - 1)
        this._element.style.transform = `translate(${x}px, ${y}px) rotate(0deg)`
        if (this._callback) {
          await this._callback()
          this._callback = null
        }
        if (this._element) this._element.style.transform = 'translate(0px, 0px) rotate(0deg)'
        return
      }

      this._element.style.transform = `translate(${currentPoint.x}px, ${currentPoint.y}px) rotate(${currentPoint.angleRadian}deg)`
      this._currentPointIndex++
      this._translate(this._callback)
    }, 1000 / this._config.fps)
  }

  public async run(initFn: (instance: AirdropGiftAnimation) => void | Promise<void>, callback?: () => void) {
    this._currentPointIndex = 0
    this._buildGraph()
    await initFn(this)
    this._translate(callback)
  }

  public pause() {
    clearTimeout(this._timeout)
  }

  public continue() {
    this._translate(this._callback)
  }

  public fitPosition() {
    this._element.style.transform = 'unset'

    const computedStyle = window.getComputedStyle(this._element)
    const position = computedStyle.getPropertyValue('position')
    if (position !== 'absolute') {
      throw new Error('fitPosition only apply for absolute position')
    }

    const currentTransition = this._element.style.transition
    this._element.style.setProperty('transition', 'unset', 'important')
    this._element.style.left = '0px'
    this._element.style.top = '0px'

    const elementRect = this._element.getBoundingClientRect()
    this._element.style.left = `${this._startPoint[0] - elementRect.left}px`
    this._element.style.top = `${this._startPoint[1] - elementRect.top}px`
    this._element.style.transition = currentTransition
  }

  public refresh() {
    this._calculateEndPoint()
    this._calculateStartPoint()
    this._calculateDistance()
    this._calculateDeg()
  }

  public kill() {
    if (this._timeout) clearTimeout(this._timeout)
    this._element = null
    this._startElement = null
    this._endElement = null
  }
}
